import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { MarkUpdatesSeenRequest, TaskUpdateFeedResponse } from '@cosvision/shared';
import {
  getTaskUpdateFeed,
  markUpdatesSeen,
  type ListFeedQuery,
} from '../lib/api/task-update-feed.js';
import { qk } from '../lib/query-keys.js';

/** CO-only: full Task Updates feed (Phase 7D). Polls every 30s. */
export function useTaskUpdatesFeedQuery(
  query: ListFeedQuery = {},
  enabled = true,
): UseQueryResult<TaskUpdateFeedResponse> {
  return useQuery({
    queryKey: qk.taskUpdateFeed.list(query),
    queryFn: () => getTaskUpdateFeed(query),
    enabled,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 29_000,
  });
}

/**
 * Thin NEW-count poll for the sidebar badge — hits the feed with
 * unreadOnly=true&limit=1 and reads the `newCount` field. 60s cadence is
 * enough for a nav-level signal.
 */
export function useTaskUpdatesNewCountQuery(enabled = true): UseQueryResult<number> {
  return useQuery({
    queryKey: qk.taskUpdateFeed.newCount(),
    queryFn: async () => {
      const result = await getTaskUpdateFeed({ unreadOnly: true, limit: 1 });
      return result.newCount;
    },
    enabled,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });
}

export function useMarkUpdatesSeenMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MarkUpdatesSeenRequest) => markUpdatesSeen(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.taskUpdateFeed.all });
      // The bell badge counts notifications too — invalidate so the
      // TASK_UPDATE_POSTED count drops in sync.
      void qc.invalidateQueries({ queryKey: qk.notifications.all });
      void qc.invalidateQueries({ queryKey: qk.inbox.all });
    },
  });
}
